/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/jsx-props-no-spreading */
import {
  Content,
  Dialog,
  DialogContainer,
  Divider,
  Heading,
  useDialogContainer,
} from "@adobe/react-spectrum";
import get from "lodash/get";
import React, { useEffect, useState } from "react";
import { useJsonForms } from "@jsonforms/react";
import { cloneDeep, set } from "lodash";
import Alert from "@spectrum-icons/workflow/Alert";
import { useSelector, useDispatch } from "react-redux";
import { setReduxState } from "../../redux/actions/SetReduxStateAction";
import JSONForm from "../../JSONForm";
import { ModalProps } from "../../types/Pages";
import Loader from "../Loader/Loader";
import styles from "./Modal.module.scss";

function ModalDialog({
  modalProps,
  setShowModal,
}: {
  modalProps: ModalProps;
  path: string;
  setShowModal?: any;
}) {
  const customBgd = modalProps?.options?.customBgd;
  const options = modalProps?.options;
  const spectrumProps = modalProps?.options?.spectrumProps;
  const style = modalProps?.options?.style;
  const titleStyle = modalProps?.options?.titleStyle;
  const contentStyle = modalProps?.options?.contentStyle;
  const alertIcon = modalProps?.options?.alertIcon;
  const reduxState = useSelector((state: any) => state?.reduxState);
  const dispatch = useDispatch();

  let dialog: any = null;
  if (!customBgd) {
    dialog = useDialogContainer();
  }

  // Read the data from the context name passed
  let data = useSelector((state: any) =>
    get(state?.reduxState, modalProps?.options?.dataContext)
  );
  if (modalProps?.data) {
    data = modalProps.data;
  }
  const [formData, setFormData] = useState<any>(data);
  const [loader, setLoader] = useState(false);

  const context = useJsonForms();

  const loaderHandler =
    context?.config?.lifecycleHooks?.onSuccessCallbacks?.loader;
  const oncancel = context?.config?.lifecycleHooks?.onCancelCallbacks?.oncancel;
  const name = options?.name;
  const onSubmit = context?.config?.lifecycleHooks?.modal?.onSubmit;

  const dismissDialog = () => {
    if (customBgd) setShowModal(false);
    else dialog.dismiss();
  };

  useEffect(() => {
    if (loaderHandler) {
      loaderHandler({ setLoader });
    }
    if (oncancel) {
      oncancel({ dismissDialog });
    }
  }, []);

  const onCancelCallback = () => {
    if (customBgd) setShowModal(false);
    else dialog.dismiss();
  };
  const onSuccessCallback = () => {
    if (customBgd) setShowModal(false);
    else dialog.dismiss();

    // Clone to avoid redux mutation error
    const clonedReduxState = cloneDeep(reduxState);
    const updatedReduxState = set(
      clonedReduxState,
      modalProps?.options?.dataContext,
      formData
    );
    dispatch(setReduxState(updatedReduxState));
    if (onSubmit) {
      onSubmit(name);
    }
  };

  const onErrorCallback = () => {
    if (customBgd) setShowModal(false);
    else dialog.dismiss();
  };

  const onChangeHandler = ({ data: updatedData }: { data: any }) => {
    setFormData(updatedData);
  };

  return (
    <Dialog
      {...spectrumProps}
      UNSAFE_className={styles.section}
      UNSAFE_style={style}
    >
      <Heading UNSAFE_style={titleStyle}>
        {options?.title}
        {alertIcon && (
          <Alert UNSAFE_className={styles.alertIcon} color="negative" />
        )}
      </Heading>
      <Divider />
      <Content UNSAFE_style={contentStyle}>
        <JSONForm
          uischema={modalProps.uischema}
          schema={modalProps.schema}
          data={formData}
          onChange={onChangeHandler}
          config={{
            onCancelCallback,
            onSuccessCallback,
            onErrorCallback,
            ...context?.config,
          }}
          renderers={context.renderers}
        />
      </Content>
      {loader && <Loader fullScreen />}
    </Dialog>
  );
}

function Modal({
  setShowModal,
  showModal,
  modalProps,
  path,
}: {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  modalProps: ModalProps;
  path: string;
}) {
  const customBgd = modalProps?.options?.customBgd;
  if (customBgd) {
    return (
      <>
        {showModal && (
          <div className={styles.overlay} data-testid="modal-container">
            <ModalDialog
              modalProps={modalProps}
              path={path}
              setShowModal={setShowModal}
            />
          </div>
        )}
      </>
    );
  }
  return (
    <DialogContainer
      onDismiss={() => setShowModal(false)}
      data-testid="modal-container"
    >
      {showModal && <ModalDialog modalProps={modalProps} path={path} />}
    </DialogContainer>
  );
}

export default Modal;

ModalDialog.defaultProps = {
  setShowModal: null,
};
